import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checklist, Drawer, Field, FieldGroup, Select } from '@itds/elements/index.js';
import { STATES } from '@itds/Constants';

import Api from '@carrier/utils/Api';

//Initializing the state for bulk changes
const initialState = {
  adb_rider_ids: [],
  band_id: '',
  child_rider_ids: [],
  child_wop_rider_ids: [],
  col_type: '',
  gender: '',
  health_category_id: '',
  modal_factor_id: '',
  product_id: '',
  rider_action: '',
  state_action: '',
  states: [],
  table_rate_id: "Don't Change", // set table_rate_id to "Don't Change" because we want to be able to send an empty string for the "None" option
  wop_rider_ids: []
};

const BulkEdit = ({
  bands,
  healthCategories,
  modalFactors,
  onClose,
  products,
  onSave,
  rates,
  riders,
  show,
  tableRates
}) => {
  const [bulk_changes, setBulkChanges] = useState(initialState);
  const [bulk_updating, setBulkUpdating] = useState(false);

  useEffect(() => {
    setBulkChanges(initialState);
  }, [show]);

  const handleInputChange = (name, e) => {
    setBulkChanges({ ...bulk_changes, [name]: e.target.value });
  };

  const handleChecklistChange = (name, selected) => {
    setBulkChanges({ ...bulk_changes, [name]: selected });
  };

  const handleBulkChange = () => {
    const {
      adb_rider_ids,
      band_id,
      child_rider_ids,
      child_wop_rider_ids,
      col_type,
      gender,
      health_category_id,
      modal_factor_id,
      product_id,
      rider_action,
      state_action,
      states,
      table_rate_id,
      wop_rider_ids
    } = bulk_changes;
    const payload = {};

    if (state_action === 'add') {
      states.forEach(state => {
        payload[`state_${state}`] = true;
      });
    } else if (state_action === 'remove') {
      states.forEach(state => {
        payload[`state_${state}`] = false;
      });
    } else if (state_action === 'replace') {
      STATES.forEach(state => {
        payload[`state_${state.value}`] = states.includes(state.value);
      });
    }

    if (rider_action === 'add') {
      payload.adb_rider_ids = adb_rider_ids;
      payload.wop_rider_ids = wop_rider_ids;
      payload.child_rider_ids = child_rider_ids;
      payload.child_wop_rider_ids = child_wop_rider_ids;
    } else if (rider_action === 'remove') {
      payload.adb_rider_ids = adb_rider_ids;
      payload.wop_rider_ids = wop_rider_ids;
      payload.child_rider_ids = child_rider_ids;
      payload.child_wop_rider_ids = child_wop_rider_ids;
    } else if (rider_action === 'replace') {
      payload.adb_rider_ids = adb_rider_ids;
      payload.wop_rider_ids = wop_rider_ids;
      payload.child_rider_ids = child_rider_ids;
      payload.child_wop_rider_ids = child_wop_rider_ids;
    }

    if (product_id) {
      payload.product_id = product_id;
    }

    if (band_id) {
      payload.band_id = band_id;
    }

    if (modal_factor_id) {
      payload.modal_factor_id = modal_factor_id;
    }

    if (health_category_id) {
      payload.health_category_id = health_category_id;
    }

    if (gender) {
      payload.gender = gender;
    }

    if (table_rate_id !== "Don't Change") {
      payload.table_rate_id = table_rate_id;
    }

    if (col_type) {
      payload.col_type = col_type;
    }

    setBulkUpdating(true);

    Promise.all(
      rates.map(rate => {
        const beforeRate = rate;
        const updated_rate = { ...rate, ...payload };

        // eslint-disable-next-line eqeqeq
        const healthCategoryObj = healthCategories.filter(
          i => i.id == updated_rate.health_category_id
        );

        if (healthCategoryObj[0].tobacco_category_type === 'Tobacco') {
          // @ts-ignore
          updated_rate.tobacco = true;
        } else {
          // @ts-ignore
          updated_rate.tobacco = false;
        }

        if (rider_action === 'add') {
          //CHILD RIDER
          let childRiderArr = updated_rate.child_rider_ids.concat(beforeRate.child_rider_ids);

          childRiderArr = childRiderArr.filter((item, index) => {
            // eslint-disable-next-line eqeqeq
            return childRiderArr.indexOf(item) == index;
          });
          updated_rate.child_rider_ids = childRiderArr;

          //WOP RIDER
          let wopRiderArr = updated_rate.wop_rider_ids.concat(beforeRate.wop_rider_ids);

          wopRiderArr = wopRiderArr.filter((item, index) => {
            // eslint-disable-next-line eqeqeq
            return wopRiderArr.indexOf(item) == index;
          });
          updated_rate.wop_rider_ids = wopRiderArr;

          //Child WOP RIDER
          let childWopRiderArr = updated_rate.child_wop_rider_ids.concat(
            beforeRate.child_wop_rider_ids
          );

          childWopRiderArr = childWopRiderArr.filter((item, index) => {
            // eslint-disable-next-line eqeqeq
            return childWopRiderArr.indexOf(item) == index;
          });
          updated_rate.child_wop_rider_ids = childWopRiderArr;

          //CHILD WOP RIDER
          let adbRiderArr = updated_rate.adb_rider_ids.concat(beforeRate.adb_rider_ids);

          adbRiderArr = adbRiderArr.filter((item, index) => {
            // eslint-disable-next-line eqeqeq
            return adbRiderArr.indexOf(item) == index;
          });
          updated_rate.adb_rider_ids = adbRiderArr;
        } else if (rider_action === 'remove') {
          //CHILD RIDER
          const childRiderArr = beforeRate.child_rider_ids.filter(id => {
            // return those elements not in payload
            return !updated_rate.child_rider_ids.includes(id);
          });

          updated_rate.child_rider_ids = childRiderArr;

          //CHILD WOP RIDER
          const childWopRiderArr = beforeRate.child_wop_rider_ids.filter(id => {
            // return those elements not in payload
            return !updated_rate.child_wop_rider_ids.includes(id);
          });

          updated_rate.child_wop_rider_ids = childWopRiderArr;

          //WOP RIDER
          const wopRiderArr = beforeRate.wop_rider_ids.filter(id => {
            // return those elements not in payload
            return !updated_rate.wop_rider_ids.includes(id);
          });

          updated_rate.wop_rider_ids = wopRiderArr;

          //ADB RIDER
          const adbRiderArr = beforeRate.adb_rider_ids.filter(id => {
            // return those elements not in payload
            return !updated_rate.adb_rider_ids.includes(id);
          });

          updated_rate.adb_rider_ids = adbRiderArr;
        }

        return Api.updateQuoteRate(rate.id, updated_rate);
      })
    ).then(() => {
      onSave();
      handleClose();
    });
  };

  const handleClose = () => {
    setBulkUpdating(false);
    onClose();
  };

  return (
    <Drawer
      alignRight={true}
      footer={
        <Button
          disabled={bulk_updating}
          icon={bulk_updating ? 'loading mdi-spin' : null}
          onClick={handleBulkChange}
          style={{ marginLeft: 'auto' }}
        >
          {bulk_updating ? 'Saving changes...' : 'Make Bulk Changes'}
        </Button>
      }
      onClose={onClose}
      show={show}
      title={`Bulk Editing ${rates.length} Quote Rates`}
    >
      <Field label='Product'>
        <Select
          onChange={handleInputChange.bind(null, 'product_id')}
          options={[{ value: '', label: "Don't Change" }].concat(
            products.map(p => ({ value: p.id, label: p.product_name }))
          )}
          value={bulk_changes.product_id}
        />
      </Field>

      <Field label='Band'>
        <Select
          onChange={handleInputChange.bind(null, 'band_id')}
          options={[{ value: '', label: "Don't Change" }].concat(
            bands.map(b => ({ value: b.id, label: b.band_name }))
          )}
          value={bulk_changes.band_id}
        />
      </Field>

      <Field label='Modal Factor'>
        <Select
          onChange={handleInputChange.bind(null, 'modal_factor_id')}
          options={[{ value: '', label: "Don't Change" }].concat(
            modalFactors.map(mf => ({ value: mf.id, label: mf.name }))
          )}
          value={bulk_changes.modal_factor_id}
        />
      </Field>

      <Field label='Health Category'>
        <Select
          onChange={handleInputChange.bind(null, 'health_category_id')}
          options={[{ value: '', label: "Don't Change" }].concat(
            healthCategories.map(hc => ({ value: hc.id, label: hc.carrier_health_category }))
          )}
          value={bulk_changes.health_category_id}
        />
      </Field>

      <Field label='Gender'>
        <Select
          onChange={handleInputChange.bind(null, 'gender')}
          options={[
            { value: '', label: "Don't Change" },
            { value: 'Both', label: 'Unisex' },
            { value: 'Male' },
            { value: 'Female' }
          ]}
          value={bulk_changes.gender}
        />
      </Field>

      <FieldGroup contained={true} label='Table Rate'>
        <Field description='If you want these rates to apply to table rated quotes, pick a table rate below.'>
          <Select
            onChange={handleInputChange.bind(null, 'table_rate_id')}
            options={[
              { value: "Don't Change", label: "Don't Change" },
              { value: '', label: 'None' }
            ].concat(tableRates.map(tr => ({ value: tr.id, label: tr.name })))}
            value={bulk_changes.table_rate_id}
          />
        </Field>

        <Field description='Only apply these rates to table rates'>
          <Select
            onChange={handleInputChange.bind(null, 'col_type')}
            options={[
              { value: '', label: "Don't Change" },
              { value: 'Normal', label: 'Normal' },
              { value: 'Table Rate', label: 'Table Rate' }
            ]}
            value={bulk_changes.col_type}
          />
        </Field>
      </FieldGroup>

      <FieldGroup contained={true} label='Riders'>
        <Field label='Action'>
          <Select
            onChange={handleInputChange.bind(null, 'rider_action')}
            options={[
              { value: '', label: "Don't Change" },
              { value: 'replace', label: 'Replace Riders' },
              { value: 'remove', label: 'Remove Riders' },
              { value: 'add', label: 'Add Riders' }
            ]}
            value={bulk_changes.rider_action}
          />
        </Field>
        <Field label='Accidental Death Benefit Riders'>
          <Checklist
            onChange={handleChecklistChange.bind(null, 'adb_rider_ids')}
            options={riders
              .filter(r => r.rider_type === 'adb')
              .map(r => ({ value: r.id, label: r.name }))}
            selected={bulk_changes.adb_rider_ids || []}
            showSelectAll={true}
          />
        </Field>

        <Field label='Child Riders'>
          <Checklist
            onChange={handleChecklistChange.bind(null, 'child_rider_ids')}
            options={riders
              .filter(r => r.rider_type === 'child')
              .map(r => ({ value: r.id, label: r.name }))}
            selected={bulk_changes.child_rider_ids || []}
            showSelectAll={true}
          />
        </Field>

        <Field label='Child + Waiver of Premium Riders'>
          <Checklist
            onChange={handleChecklistChange.bind(null, 'child_wop_rider_ids')}
            options={riders
              .filter(r => r.rider_type === 'child_wop')
              .map(r => ({ value: r.id, label: r.name }))}
            selected={bulk_changes.child_wop_rider_ids || []}
            showSelectAll={true}
          />
        </Field>

        <Field label='Waiver of Premium Riders'>
          <Checklist
            onChange={handleChecklistChange.bind(null, 'wop_rider_ids')}
            options={riders
              .filter(r => r.rider_type === 'wop')
              .map(r => ({ value: r.id, label: r.name }))}
            selected={bulk_changes.wop_rider_ids || []}
            showSelectAll={true}
          />
        </Field>
      </FieldGroup>

      <FieldGroup contained={true} label='States'>
        <Field label='Action'>
          <Select
            onChange={handleInputChange.bind(null, 'state_action')}
            options={[
              { value: '', label: "Don't Change" },
              { value: 'replace', label: 'Replace States' },
              { value: 'remove', label: 'Remove States' },
              { value: 'add', label: 'Add States' }
            ]}
            value={bulk_changes.state_action}
          />
        </Field>
        <Checklist
          columns={2}
          onChange={handleChecklistChange.bind(null, 'states')}
          options={STATES}
          selected={bulk_changes.states || []}
          showSelectAll={true}
        />
      </FieldGroup>
    </Drawer>
  );
};

BulkEdit.propTypes = {
  bands: PropTypes.array,
  healthCategories: PropTypes.array,
  modalFactors: PropTypes.array,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  products: PropTypes.array,
  rates: PropTypes.array,
  riders: PropTypes.array,
  show: PropTypes.bool,
  tableRates: PropTypes.array
};

export default BulkEdit;
